import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AvisPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    comment: '',
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/testimonials`);
      setTestimonials(data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.comment) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      await axios.post(`${API}/testimonials`, formData);
      toast.success('Merci pour votre avis ! Il sera publié après validation.');
      setFormData({ name: '', rating: 5, comment: '' });
      setShowForm(false);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error('Erreur lors de l\'envoi de votre avis');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-16">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h1
          className="text-5xl md:text-6xl font-light text-center mb-4 text-[#5C2018]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          data-testid="avis-title"
        >
          Avis Clients
        </h1>
        <p className="text-center text-[#332724] font-light mb-12 max-w-2xl mx-auto">
          Découvrez ce que nos clients disent de leur expérience chez Harmony Parfumerie.
        </p>

        {/* Google Reviews CTA */}
        <div className="max-w-3xl mx-auto bg-white border border-gold rounded-lg p-6 mb-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#B8922A]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Star className="text-[#B8922A] fill-[#B8922A]" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-[#5C2018]">Retrouvez-nous sur Google</h3>
              <p className="text-sm text-[#332724] font-light">
                Consultez tous nos avis ou laissez le vôtre directement sur Google
              </p>
            </div>
          </div>
          <a
            href="https://www.google.com/search?sca_esv=25795b5d4f09c1a9&q=avis+sur+maison+harmony+-+parfumerie+b%C3%A9ziers&uds=ALYpb_ncDc7jTlmw6Mmq7NjuX5c-YspAyyO1LAGnz_sZMslwz50Km9V0V_Ljjdb1cJYBG9ScWZ846YLPEirq1yjRc81MNciiXo-_p9ZqpdP8xG_AUgCAS_l-ePOZYuxJWfWIp1sjnkEAWmTqaTu0G6i5K245UzFeS674C8GRGi0RvDkQu9xbFAGwM6j3wyK_MwpsNb6xtEWpTQYd7Z5KQrWoZ84HUjoyaydzyLoxdsNOv2DeigRuejxA1sEfwNV94E6C-zNI9Tq01lRhoryywDkegRsHzux5cKOxHK41e0Su19BMpwcdp24qA6EFSLezEdnPL6f2y9FmgKFvqNv_l07kGQeUzCzlpffHy_aD_2R5CPaZXeT20sAdaXaNFwXVrFtzVcbetfp3oRFzd2fArb0PqE_vM4LJlQbJ-onancmM-qPKDs1_hYI"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary px-6 py-2 rounded-full text-sm uppercase tracking-[0.2em] whitespace-nowrap"
            data-testid="google-reviews-cta"
          >
            Voir les avis Google
          </a>
        </div>

        <div className="text-center mb-12">
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm"
            data-testid="add-testimonial-btn"
          >
            {showForm ? 'Annuler' : 'Laisser un avis'}
          </button>
        </div>

        {showForm && (
          <div className="max-w-2xl mx-auto mb-12 bg-white p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]" data-testid="testimonial-form">
            <h3
              className="text-2xl mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Votre avis
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Votre nom *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                  required
                  data-testid="testimonial-name"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm uppercase tracking-[0.2em] mb-2 text-[#332724]">
                  Note
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="transition-colors"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        size={32}
                        className={star <= formData.rating ? 'fill-[#B8922A] text-[#B8922A]' : 'text-[#332724]/20'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <textarea
                  placeholder="Votre commentaire *"
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows="4"
                  className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                  required
                  data-testid="testimonial-comment"
                />
              </div>

              <button
                type="submit"
                className="w-full btn-primary px-8 py-3 rounded-full font-medium uppercase tracking-[0.2em] text-sm"
                data-testid="testimonial-submit"
              >
                Envoyer mon avis
              </button>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E7A6A] mx-auto"></div>
            <p className="mt-4 text-[#332724]">Chargement...</p>
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#332724] text-lg">Aucun avis pour le moment. Soyez le premier à en laisser un !</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white p-6 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]"
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={18}
                      className={i < testimonial.rating ? 'fill-[#B8922A] text-[#B8922A]' : 'text-[#332724]/20'}
                    />
                  ))}
                </div>
                <p className="text-[#332724] font-light mb-4 leading-relaxed">"{testimonial.comment}"</p>
                <div className="flex justify-between items-center text-sm">
                  <p className="font-medium text-[#5C2018]">{testimonial.name}</p>
                  <p className="text-[#332724]/70 text-xs">{formatDate(testimonial.date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvisPage;
